import { forwardRef, type ReactNode } from 'react';

import { cva, cx } from 'cva';
import {
  GridListItem,
  type GridListItemProps,
} from 'react-aria-components/GridList';
import { Link, type LinkProps } from 'react-aria-components/Link';

import { Text } from '../text/text';

import styles from './card_item.module.css';

export type CardMediaProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

/** Card Media, typically displaying a `Thumbnail` and optionally `CardOverlayRow`. */
export const CardMedia = ({ className, children, ...rest }: CardMediaProps) => {
  const classes = cx(styles.card_media, className);
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
};

export type CardOverlayRowProps = {
  children?: ReactNode;
  className?: string;
};

/** Card overlay row, to present items on top of `CardMedia`. */
export const CardOverlayRow = ({
  children,
  className,
}: CardOverlayRowProps) => {
  const classes = cx(styles.card_overlay_row, className);
  return <div className={classes}>{children}</div>;
};

export type CardFooterProps = {
  children?: ReactNode;
  className?: string;
};

/** Card footer, typically containing a `CardTitle` and `ContextMenuButton` */
export const CardFooter = ({ children, className }: CardFooterProps) => {
  return <div className={cx(styles.card_footer, className)}>{children}</div>;
};

export type CardTitleProps = {
  href?: LinkProps['href'];
  children?: ReactNode;
  target?: LinkProps['target'];
};

/** Styled text for used in `CardFooter`. */
export const CardTitle = ({ href, children, target }: CardTitleProps) => {
  const textValue = typeof children === 'string' ? children : undefined;
  const content = (
    <Text
      className={styles.card_title}
      color="high_emphasis"
      elementType="h5"
      variant="value"
      noWrap
      title={textValue}
    >
      {children}
    </Text>
  );

  if (href) {
    return (
      <Link className={styles.card_title_link} href={href} target={target}>
        {content}
      </Link>
    );
  }

  return content;
};

export const cardVariants = cva({
  base: styles.card,
});

export type CardItemProps = GridListItemProps;

/**
 * CardItem - A card item that can be used in a `CardView`.
 *
 * See https://react-spectrum.adobe.com/react-aria/GridList.html for more details.
 *
 * ```tsx
 * <CardItem
 *   id={id}
 *   textValue={title}
 * >
 *   <CardMedia>
 *     <Thumbnail size="cover" url={item.image_url} alt="" />
 *   </CardMedia>
 *   <CardFooter>
 *     <CardTitle>{title}</CardTitle>
 *   </CardFooter>
 * </CardItem>
 * ```
 */
export const CardItem = forwardRef<HTMLDivElement, CardItemProps>(
  ({ children, className, ...rest }, ref) => {
    const classes = cardVariants({
      className,
    });
    const textValue = typeof children === 'string' ? children : undefined;

    return (
      <GridListItem
        textValue={textValue}
        {...rest}
        ref={ref}
        className={classes}
      >
        {children}
      </GridListItem>
    );
  },
);
